import { AppComponent } from 'src/app/app.component';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';

import * as moment from 'moment';

import { IoService } from '../io/io.service';
import { LocationService } from '../location/location.service';
import { TagPrintService } from '../tag-print/tag-print.service';
import { DateRange, DatePickerConfig } from 'src/app/modules/report/filter-date-range/date-config-interface';
import { LoadingComponent } from 'src/app/modules/shared/loading/loading.component';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  private reportCategories;
  private reportsByKey = {};
  private activeReportConfig;

  private sorts;
  private activeFields;
  private search: string;
  private valueFilters;

  private records;
  private totals;

  private chunkLoaderTimeout;

  // Latest-wins gate for scroll-chunk loads: at most one chunk request is in
  // flight at a time. Scrolls arriving while one is running only overwrite
  // pendingChunkSkip; when the in-flight request settles, at most one follow-up
  // load fires, for the newest position still missing data.
  private chunkLoadInFlight = false;
  private pendingChunkSkip: number | null = null;
  private searchTextTimeout;

  private detailDocumentId;
  private detailDocumentMeta;
  private adding = false;

  private reportsLoaded = new Subject<any>();
  private reportSelected = new Subject<any>();
  private reportLoaded = new Subject<any>();
  private recordsUpdated = new Subject<any>();
  // Emits a user-facing message when a report fails to load (e.g. a report that
  // requires a single location to be selected), or null when the load succeeds.
  private loadError = new Subject<any>();
  private valueFiltersUpdated = new Subject<any>();

  private sortSet = new Subject<any>();
  private detailDocumentSet = new Subject<any>();
  private addingSet = new Subject<any>();
  private variantsChanged = new Subject<{ reportKey: string }>();
  private variantApplied = new Subject<{ reportKey: string, variantKey: string | null }>();

  // Tracks the currently applied variant for the active report (null = system default)
  private activeVariantKey: string | null = null;

  private stagedConfig;
  private loadingTimeout: number | undefined;
  private initPromise: Promise<void>;

  // Monotonic "view generation". Bumped by every full (skip-less) load — i.e. a
  // report/variant/filter/search switch. Each in-flight loadRecords request captures
  // the generation it was issued under; responses whose generation has since been
  // superseded are discarded. This prevents a late response (especially a scroll-chunk
  // load) from a previously-active report writing its rows into the now-active report.
  private loadGeneration = 0;



  private state: any = {
    initializing: false,
    initialized: false,
    dateRange: new DateRange(null, null)
  };

  // Current sort state (null when the report's default order applies); read by
  // report-data when creating a scheduled report from the unsaved on-screen view.
  getSorts() {
    return this.sorts;
  }

  constructor(
    private router: Router,
    private ioService: IoService,
    private tagPrintService: TagPrintService,
    private locationService: LocationService
  ) { }

  async init() {
    if (this.state.initialized) {
      return;
    }

    if (!this.initPromise) {
      console.log('*** report service init');
      this.initPromise = Promise.all([
        this.loadReports(),
        this.loadUserDefaults()
      ]).then(() => {
        this.state.initialized = true;
      }).catch((err) => {
        // Don't cache a rejected init — a failed boot load (e.g. offline) would
        // otherwise poison every later loadReport until a full page reload.
        this.initPromise = null;
        throw err;
      });
    }

    await this.initPromise;
  }

  
  // The user's admin-set default time filter (User.defaultTimeFilter, a named
  // picker range like 'Today'). Loaded once per app boot alongside the report
  // list; a failed load is treated as unset so reports fall back to the
  // standard This Month default.
  private defaultTimeFilter: string = null;

  loadUserDefaults(): Promise<void> {
    return this.ioService.post('/user/getUserDefaults').then((defaults: any) => {
      this.defaultTimeFilter = defaults?.defaultTimeFilter || null;
    }).catch(() => {
      this.defaultTimeFilter = null;
    });
  }

  // The date range seeded when no range has been chosen yet this session (i.e.
  // once per login/refresh, on the first report activation). The named ranges
  // are computed fresh from the current moment, so 'Today' is always the
  // current day. An unset or unrecognized default keeps the existing This
  // Month behavior. Reports without a date picker (no datePath) never apply
  // this range, and later manual picker changes are never overridden.
  private initialDateRange(): DateRange {
    if (this.defaultTimeFilter) {
      const range = new DatePickerConfig().ranges[this.defaultTimeFilter];
      if (range) {
        return new DateRange(range[0], range[1]);
      }
    }
    return new DateRange(
      moment().startOf('month'),
      moment().endOf('month')
    );
  }

  loadReports(): Promise<void> {
    return this.ioService.post('/report/getLocationReports', {
      // locationId: this.locationService.getActiveLocation()._id
    }).then((reportsData: any) => {

      // Build List of Category Names
      const categoryNames = [];
      for (const report of reportsData) {
        this.reportsByKey[report.key] = report;
        if (report.category && categoryNames.indexOf(report.category) == -1) {
          categoryNames.push(report.category);
        }
      }

      // Sort Category Names
      categoryNames.sort();

      // Add Other Category to the end
      categoryNames.push('Other');

      const categories = [];
      for (const categoryName of categoryNames) {
        categories.push({
          name: categoryName,
          options: [],
          active: true
        });
      }

      // Sort Reports By Name
      reportsData.sort((a, b) => (a.name > b.name) ? 1 : -1);

      for (const report of reportsData) {

        if (report.type != 'document') {

          if (!report.category) {
            report.category = 'Other';
          }

          for (const category of categories) {
            if (report.category == category.name) {
              category.options.push(report);
            }
          }
        }
      }

      this.reportCategories = categories;

      this.reportsLoaded.next({
        reportCategories: this.reportCategories
      });
    });
  }



  async getReports(reportTypes, contextType) {
    if ( !this.reportsByKey ||  Object.entries(this.reportsByKey).length === 0) {
      await this.loadReports();
    }
    const reports = [];

    for (const [reportKey, reportConfig] of Object.entries(this.reportsByKey)) {
      console.log(reportConfig);
      if (reportTypes.indexOf(reportConfig['type']) != -1) {
        if (!contextType || (reportConfig['contextType'] && reportConfig['contextType'].indexOf(contextType) != -1)) {
          // TODO: Check permission ?
          reports.push(reportConfig);
        }
      }
    }
    return reports;
  }



  getReportCategories() {
    return this.reportCategories;
  }

  // The report that LISTS transfers of a type: an explicit
  // PurchaseType.listReportKey wins, otherwise the report whose addLink points
  // at transfers/<typeKey> (listed ones first). Only reports the user can open
  // are considered — the report list is permission-filtered server-side.
  async findReportForTransferType(typeKey: string, preferredKey: string = null): Promise<any> {
    if (!typeKey) {
      return null;
    }
    await this.init();
    if (preferredKey && this.reportsByKey[preferredKey]) {
      return this.reportsByKey[preferredKey];
    }
    const link = 'transfers/' + typeKey;
    const matches: any[] = Object.values(this.reportsByKey).filter((report: any) => report.addLink === link);
    return matches.find(report => !report.unlisted && report.category !== 'Hidden') || matches[0] || null;
  }



  reportsLoadedObservable(): Observable<any> {
    return this.reportsLoaded.asObservable();
  }



  loadFilteredReport(key, valueFilters) {
    const pathParts = this.router.url.split('/');

    this.stagedConfig = {
      valueFilters
    };

    if (this.activeReportConfig.key == key) {
      // this.loadReport(null);
      this.setActiveReport(this.activeReportConfig);
    } else {
      this.router.navigate(['/reports/' + key + '/' + pathParts.slice(3).join('/')]);
    }
  }

  // setReportByKey(key) {
  //   // if (!this.reportsByKey[key]) {
  //   //   this.pendingReportKey = key;
  //   // } else {
  //   //   this.setActiveReport(this.reportsByKey[key]);
  //   // }

  //   this.setActiveReport({
  //     key
  //   });
  // }

  getReportIdByKey(key) {
    if (this.reportsByKey[key]) {
      return this.reportsByKey[key]._id;
    } else {
      return false;
    }
  }

  /**
   * Activates a report. Optional `variantKeyArg`:
   *   undefined  → auto: user's default variant if set, else system default
   *   null or 'system' → system default
   *   <key>      → that named variant (or system default if not found)
   */
  setActiveReport(reportConfig, valueFilters = [], variantKeyArg?: string | null) {
    // Cancel any pending scroll-chunk / search-debounce timers from the previous
    // report — without this, their callbacks fire against the now-nulled state
    // (template, etc.) and throw "Cannot read properties of null".
    if (this.chunkLoaderTimeout) {
      clearTimeout(this.chunkLoaderTimeout);
      this.chunkLoaderTimeout = null;
    }
    // A queued follow-up chunk position belongs to the previous report.
    this.pendingChunkSkip = null;
    if (this.searchTextTimeout) {
      clearTimeout(this.searchTextTimeout);
      this.searchTextTimeout = null;
    }

    this.activeReportConfig = reportConfig;
    this.activeFields = null;
    this.records = null;
    this.totals = null;
    this.search = null;
    this.state.skip = null;
    this.state.template = null;

    if (!this.state.dateRange?.startDate) {
      this.state.dateRange = this.initialDateRange();
    }

    // A report may name the picker range it opens with (template.defaultDateRange,
    // e.g. 'Last 7 Days' for Sales by Hour); it applies every time that report is
    // activated and the picker can still be changed afterwards.
    const reportDefaultRange = reportConfig?.defaultDateRange
      || this.reportsByKey[reportConfig?.key]?.defaultDateRange;
    if (reportDefaultRange) {
      const named = new DatePickerConfig().ranges[reportDefaultRange];
      if (named) {
        this.state.dateRange = new DateRange(named[0], named[1]);
      }
    }

    // Resolve which variant (if any) to apply
    let variantToApply: any = null;
    let resolvedVariantKey: string | null = null;
    if (variantKeyArg === 'system' || variantKeyArg === null) {
      resolvedVariantKey = null;
    } else if (typeof variantKeyArg === 'string' && variantKeyArg) {
      variantToApply = this.findVariant(reportConfig.key, variantKeyArg);
      resolvedVariantKey = variantToApply ? variantKeyArg : null;
    } else {
      // auto: user's default if set
      const defaultKey = this.getDefaultVariantKey(reportConfig.key);
      if (defaultKey) {
        variantToApply = this.findVariant(reportConfig.key, defaultKey);
        resolvedVariantKey = variantToApply ? defaultKey : null;
      }
    }
    this.activeVariantKey = resolvedVariantKey;

    if (variantToApply) {
      this.sorts = (Array.isArray(variantToApply.sorts) && variantToApply.sorts.length > 0)
        ? variantToApply.sorts.map(s => ({ path: s.path, direction: s.direction === -1 ? -1 : 1 }))
        : null;
      this.valueFilters = (Array.isArray(variantToApply.valueFilters)
        ? variantToApply.valueFilters.map(f => ({ ...f })) : []).concat(valueFilters);
      this.activeReportConfig.clientConfig = this.activeReportConfig.clientConfig || {};
      this.activeReportConfig.clientConfig.activeFields = Array.isArray(variantToApply.activeFields)
        ? [...variantToApply.activeFields] : null;
    } else {
      this.sorts = null;
      this.valueFilters = valueFilters;
      if (this.activeReportConfig.clientConfig) {
        delete this.activeReportConfig.clientConfig.activeFields;
      }
    }

    if (this.stagedConfig) {
      if (this.stagedConfig.valueFilters) {
        this.valueFilters = this.valueFilters.concat(this.stagedConfig.valueFilters);
      }

      if (this.stagedConfig.activeFields) {
        this.activeReportConfig.clientConfig = this.activeReportConfig.clientConfig || {};
        this.activeReportConfig.clientConfig.activeFields = this.stagedConfig.activeFields;
      }

      if (this.stagedConfig.name) {
        this.activeReportConfig.nameOverride = this.stagedConfig.name;
      }

      this.stagedConfig = null;
    }

    this.valueFiltersUpdated.next(this.valueFilters);
    this.sortSet.next(this.sorts);
    this.reportSelected.next(this.activeReportConfig);
    this.variantApplied.next({ reportKey: reportConfig.key, variantKey: this.activeVariantKey });
    // this.recordsUpdated.next(this.records);
    this.loadReport(null);
  }

  getActiveReportConfig() {
    return this.activeReportConfig;
  }

  filterByFieldValue(field, value, displayValue?) {
    console.log('filterByFieldValue:');
    console.log('field: ', field);
    console.log('value: ', value);

    this.valueFilters.push({
      name: field.name,
      path: field.path,
      type: field.type,
      value,
      displayValue
    });

    this.refresh();
    this.valueFiltersUpdated.next(this.valueFilters);
  }

  removeValueFilter(filterIndex) {
    this.valueFilters.splice(filterIndex, 1);
    this.refresh();
    this.valueFiltersUpdated.next(this.valueFilters);
  }

  loadReport = async (skip = null) => {

      console.log('LOAD REPORT');

      // A full (skip-less) load establishes a new view generation. Any response —
      // initial or scroll-chunk — issued under a superseded generation is stale (the
      // user switched report/variant/filter/search) and must be discarded so it can't
      // write a prior report's rows into the now-active report. Chunk loads (skip > 0)
      // belong to the current generation and don't bump it.
      if (!skip) {
        this.loadGeneration++;
        // A full reload supersedes any pending scroll-chunk load — cancel it so a
        // stale scroll timer can't fire a duplicate loadReport after a
        // filter / sort / search / variant change.
        if (this.chunkLoaderTimeout) {
          clearTimeout(this.chunkLoaderTimeout);
          this.chunkLoaderTimeout = null;
        }
        // Likewise drop any queued follow-up chunk — the viewport resets to the
        // top on a full load, so the remembered position is stale.
        this.pendingChunkSkip = null;
        // ...and the stored scroll offset itself. Chunk loads leave state.skip
        // at the last chunk's offset; a full reload that didn't clear it (add /
        // remove field, sort, filter) went out WITH that offset, and the server
        // only computes totals for offset-less loads — so the totals row
        // vanished after adding a column to a scrolled report until refresh.
        this.state.skip = null;
      }
      const generation = this.loadGeneration;

      if (skip) {
        this.state.skip = skip;
      }

      try {
        await this.init();
      } catch (err) {
        // init() rejecting (report list failed to load) would otherwise skip the
        // finally below and strand the loading overlay.
        if (generation === this.loadGeneration) {
          LoadingComponent.hideLoading();
          this.loadError.next('Unable to load reports. Please check your connection and try again.');
        }
        return;
      }

      // The active report may not have been set yet (callers can race during
      // app boot), or it may have been cleared between scheduling and running.
      // Either way, there's nothing to ask the server for.
      if (!this.activeReportConfig) {
        LoadingComponent.hideLoading();
        return;
      }

      // The report list is loaded once per session, but the dashboard's tiles
      // are fetched fresh on every visit — so a report added after this session
      // began opens with no cached config. Its first request then had no
      // datePath, hence no date filter: standard reports quietly loaded
      // unfiltered, and custom handlers that require a range (e.g. Manual
      // Discounts by User) returned nothing until a refresh. Reload the list
      // once when the active key is unknown.
      const activeKey = this.activeReportConfig.key;
      if (activeKey && !this.reportsByKey[activeKey]) {
        try {
          await this.loadReports();
        } catch (err) {
          console.log('report list refresh for unknown key failed: ', err);
        }
      }

      let loadRecordsInput = this.getLoadRecordsInput();
      console.log('loadRecordsInput: ', loadRecordsInput);

      // Clear any prior load message before attempting this load.
      this.loadError.next(null);

      // Retrieve data from the server. Returned so callers (loadChunk) can tell
      // when the request settles; the catch below means it never rejects.
      return this.ioService.post('/report/loadRecords', loadRecordsInput).then((reportData: any) => {

        // Drop responses superseded by a newer full load (see loadGeneration above).
        if (generation !== this.loadGeneration) {
          return;
        }

        // Defensive: ignore a response whose report doesn't match the active one.
        if (reportData?.template?.key && this.activeReportConfig?.key
            && reportData.template.key !== this.activeReportConfig.key) {
          return;
        }

        if (reportData.template) {
          this.state.template = reportData.template;
        }

        if (skip == null) {

          // This is initial load of this report config
          this.activeFields = [];
          reportData.template.activeFields.forEach((fieldPath, fp) => {
            reportData.template.fields.forEach((fieldConfig, fc) => {
              if (fieldPath == fieldConfig.path) {

                if (this.sorts && this.sorts[0].path == fieldPath) {
                  fieldConfig.sort = this.sorts[0].direction;
                } else {
                  fieldConfig.sort = null;
                }

                this.activeFields.push(fieldConfig);
                return false;
              }
            });
          });

          this.totals = reportData.totals;

          this.reportLoaded.next({
            activeFields: this.activeFields,
            totals: this.totals,
            template: reportData.template,
            templateOptions: reportData.templateOptions,
            printTemplates: reportData.printTemplates || [],
            downloadTemplates: reportData.downloadTemplates || [],
            nameOverride: this.activeReportConfig?.nameOverride || null,
          });

          // This is the initial load, set the records to an array of empty objects
          if (this.totals) {
            this.records = Array(this.totals.count).fill({});
          }
        }

        // Put returned records into the placeholder array
        let index = 0;
        if (! reportData || !reportData.skip) {
          reportData.skip = 0;
        }
        if (!this.records) {
          this.records = [];
        }

        for (const record of reportData.records) {
          this.records[reportData.skip + index] = record;
          index++;
        }

        // console.log('records$: ', this.records);
        let records = [];
        if (this.records) {
          records = [...this.records];
        }

        this.recordsUpdated.next(records);
        this.loadError.next(null);
      })
      .catch( err => {
        // alert( err.message );
        // alert( 'Error loading report data. Please try again. If the problem persists, please verify that you have a consistent internet connection.' );
        if (generation !== this.loadGeneration) {
          return;
        }
        // Surface a load failure to the report-data area instead of leaving it
        // blank. Custom-report errors are wrapped by the server, so prefer the
        // nested message (e.g. "Please select a single location ...").
        const message = err?.error?.error?.msg || err?.error?.msg || err?.msg || null;
        this.loadError.next(message);
        // On a full-load failure with a message, clear stale rows so the message
        // isn't hidden behind a previous location's results.
        if (!skip && message) {
          this.records = null;
        }
        this.recordsUpdated.next(this.records);
      })
      .finally(() => {
        // A stale response finishing must not clear the loading state out from under
        // the newer load that superseded it.
        if (generation !== this.loadGeneration) {
          return;
        }
        if ( this.loadingTimeout ) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = undefined;
        }
        LoadingComponent.hideLoading();
      });
    // }
  }

  /**
   * Renders the active report through a print template (type: 'print') using the
   * currently displayed settings (filters, sort, search, date range, active fields).
   * Resolves to the server-rendered HTML string. The server ignores skip/limit when
   * download is set, so every matching record is included.
   */
  getPrintRender(templateKey: string): Promise<string> {
    const input = this.getLoadRecordsInput();
    delete input.clientConfig.skip;
    input.templateKey = templateKey;
    input.clientConfig.download = true;
    return this.ioService.post('/report/loadRecords', input).then((res: any) => res.rendered);
  }

  // Rows for a chart: everything the current view matches (filters, location,
  // date range, search), not just the loaded page. A very large explicit
  // limit is used rather than a download so the API returns JSON records.
  static readonly ALL_ROWS_LIMIT = 1000000;

  loadAllRecords(): Promise<{ records: any[] }> {
    const input = this.getLoadRecordsInput();
    delete input.clientConfig.skip;
    input.clientConfig.limit = ReportService.ALL_ROWS_LIMIT;
    input.clientConfig.omitTotals = true;
    return this.ioService.post('/report/loadRecords', input).then((res: any) => ({ records: (res && res.records) || [] }));
  }

  refreshRecord(index, data) {
    console.log('refreshRecord: ', index, data);

    if (this.records && this.records[index]) {
      console.log('non-refreshed record: ', this.records[index]);
      console.log('prev: '+this.records[index].name, this.records[index].timeUpdated);
      console.log('new: '+data.name, data.timeUpdated);

      // console.log('previous value: ', this.records[index].name);
      // console.log('found record to update:', index, data.name);
      this.records[index] = data;
      console.log('new value: ', this.records[index]);
      this.recordsUpdated.next(this.records);
    }
  }

  saveField(record, field) {

    console.log('saveField: ', record, field);

    delete record.editingField;
    record.saving = true;

    // switch (field.type) {
    //   case 'currency':
    //     record[field.path] = record[field.path]*100;
    //     break;
    // }

    let locationId = this.locationService.getActiveLocation()._id;
    if (record.locationId && !Array.isArray(record.locationId)) {
      locationId = record.locationId;
    }

    // See if the value actually changed
    if (record.initialValue != record[field.path]) {
      const reportId = this.activeReportConfig?._id || this.getReportIdByKey(this.activeReportConfig?.key);
      this.ioService.post(field.instantEditSavePath, {
        _id: record._id,
        field: field.path,
        value: record[field.path],
        locationId,
        reportId,
        activeFields: this.activeFields.map((field) => field.path)
      }).then((response: any) => {
        record.saving = false;
        let records = [...this.records];
        let recordIndex = records.findIndex(r => r._id == response.updatedRow._id);

        console.log('records: ', records);
        console.log('updatedRow._id', response.updatedRow._id);
        console.log('recordIndex: ', recordIndex);

        records[recordIndex] = response.updatedRow;
        this.recordsUpdated.next(records);
      });
    } else {
      record.saving = false;
    }
  }

  reportScrolled(skip) {
    // Scrolling to the top (index 0) never needs an additional load — the full load
    // already covers the first chunk. Guarding this also stops the programmatic
    // viewport.scrollToIndex(0) performed on every (re)load from firing a duplicate
    // request: reportScrolled(0) → loadReport(0), and loadReport treats 0 as a full
    // reload. Without this, filtering after scrolling fires loadRecords twice.
    if (!skip) return;
    if (this.state.template) {
      if (this.chunkLoaderTimeout) {
        clearTimeout(this.chunkLoaderTimeout);
      }

      this.chunkLoaderTimeout = setTimeout(() => {
        // The active report may have changed (and template nulled) while this
        // timer was pending — bail rather than dereference a null template.
        if (!this.state.template) return;

        if (!this.chunkNeeded(skip)) return;

        if (this.chunkLoadInFlight) {
          // A chunk request is already running — only remember the newest
          // position, so rapid scrolling can't stack requests. It gets a single
          // follow-up load (if still needed) once the current request settles.
          this.pendingChunkSkip = skip;
        } else {
          this.loadChunk(skip);
        }
      }, 200);
    }
  }

  // See if we need to load more data for the span starting at `skip`
  private chunkNeeded(skip): boolean {
    let needMore = true;

    let chunkSize = this.state.template.chunkSize || false;

    if (chunkSize) {
      let checkSpan = 100; // This is based on number of rows visible on the screen. This could be made dynamic later.
      needMore = false;

      if (this.records && this.records.length > 0) {
        for (let i = skip; i < skip + checkSpan; i++) {
          if (!this.records[i] || !this.records[i]._id) {
            needMore = true;
            break;
          }
        }
      }
    }

    return needMore;
  }

  private loadChunk(skip) {
    this.chunkLoadInFlight = true;
    this.loadReport(skip).finally(() => {
      this.chunkLoadInFlight = false;
      const pending = this.pendingChunkSkip;
      this.pendingChunkSkip = null;
      if (pending != null && this.state.template && this.chunkNeeded(pending)) {
        this.loadChunk(pending);
      }
    });
  }

  reportSelectedObservable(): Observable<any> {
    return this.reportSelected.asObservable();
  }

  reportLoadedObservable(): Observable<any> {
    return this.reportLoaded.asObservable();
  }

  recordsUpdatedObservable(): Observable<any> {
    return this.recordsUpdated.asObservable();
  }

  loadErrorObservable(): Observable<any> {
    return this.loadError.asObservable();
  }

  valueFiltersUpdatedObservable(): Observable<any> {
    return this.valueFiltersUpdated.asObservable();
  }

  // Date Range
  setDateRange(dateRange: DateRange) {
    this.state.dateRange = dateRange;
    this.refresh();
  }

  getDateRange() {
    return this.state.dateRange;
  }

  // Sort
  setSort(field) {
    let direction = 1;

    if (this.sorts && this.sorts[0] && field.path === this.sorts[0].path) {
      direction = this.sorts[0].direction * -1;
    }

    this.sorts = [{
      path: field.path,
      direction
    }];

    this.sortSet.next(this.sorts);

    this.refresh();
  }

  sortSetObservable(): Observable<any> {
    return this.sortSet.asObservable();
  }

  setFieldsSequence(fields) {
    this.activeFields = fields;
    this.loadReport(null);
  }

  // Persist the user's per-report column width overrides ({path, width}[]) to
  // their profile so drag-resized widths follow them across devices. An empty
  // array clears every override for the report.
  saveColumnWidths(reportKey, widths) {
    return this.ioService.post('/report/saveColumnWidths', {
      reportKey,
      widths,
    });
  }

  addField(field) {
    this.activeFields.push(field);
    this.loadReport(null);
  }

  removeField(field) {
    const activeIndex = this.activeFields.indexOf(field);
    if (activeIndex > -1) {
      this.activeFields.splice(activeIndex, 1);
    }
    this.loadReport(null);
  }


  // Search
  setSearch(searchText) {
    this.totals = null;
    this.records = null;
    this.recordsUpdated.next(this.records);
    this.sorts = null;
    this.sortSet.next(this.sorts);

    if (this.activeReportConfig) {
      if (this.searchTextTimeout) {
        clearTimeout(this.searchTextTimeout);
      }

      this.searchTextTimeout = setTimeout(() => {
        this.search = searchText;
        this.totals = null;
        this.records = null;
        this.state.skip = null;
        this.recordsUpdated.next(this.records);
        this.loadReport(null);
      }, 1000);
    }
  }



  // Active Detail
  // setDetailDocumentId(id) {
  //   console.log('setDetailDocumentId: ', this.activeReportConfig);
  //   this.setDetailDocument({
  //     id: id,
  //     name: this.activeReportConfig.name,
  //     type: this.activeReportConfig.itemType
  //   });
  // }
  setDetailDocumentId(id) {
    this.detailDocumentId = id;
  }

  getDetailDocumentId() {
    return this.detailDocumentId;
  }

  // setDetailDocument(documentMeta) {
  //   this.detailDocumentMeta = documentMeta;
  //   this.detailDocumentSet.next(this.detailDocumentMeta);
  // }

  // getDetailDocumentId() {
  //   if (this.detailDocumentMeta) {
  //     return this.detailDocumentMeta.id;
  //   } else {
  //     return null;
  //   }
  // }

  detailDocumentSetObservable(): Observable<any> {
    return this.detailDocumentSet.asObservable();
  }


  setAdding(adding) {
    this.adding = adding;
    this.addingSet.next(this.adding);
  }

  addingSetObservable(): Observable<any> {
    return this.addingSet.asObservable();
  }

  // ─── In-report Navigation Helpers ──────────────────────────────────

  // Close a detail panel by walking `depth` levels up the route tree while preserving
  // the current URL's query params (notably ?variant=). All detail components should
  // route their close() through this so the active variant survives the panel close.
  closeDetailPanel(relativeTo: ActivatedRoute, depth: number = 2): Promise<boolean> {
    return this.router.navigate(['../'.repeat(depth)], {
      relativeTo,
      queryParamsHandling: 'preserve',
    });
  }

  // The active report's template (the Report doc) — e.g. so detail panels can
  // read config keys like itemViewKey.
  getActiveTemplate() {
    return this.state.template;
  }

  // Hop to another record/detail within the same report while preserving ?variant=.
  navigateWithinReport(commands: any[], relativeTo: ActivatedRoute): Promise<boolean> {
    return this.router.navigate(commands, {
      relativeTo,
      queryParamsHandling: 'preserve',
    });
  }

  // Navigate into a report from outside (dashboard, side menu, etc.). Resolves the
  // user's default variant (if any) and includes it in the URL so the report loads
  // straight into the right view without an intermediate redirect.
  async navigateToReport(reportKey: string): Promise<boolean> {
    await this.init();
    const userDefault = this.getDefaultVariantKey(reportKey);
    return this.router.navigate(
      ['reports', reportKey],
      userDefault ? { queryParams: { variant: userDefault } } : {},
    );
  }

  // ─── Report Variants ──────────────────────────────────────────────

  variantsChangedObservable(): Observable<{ reportKey: string }> {
    return this.variantsChanged.asObservable();
  }

  variantAppliedObservable(): Observable<{ reportKey: string, variantKey: string | null }> {
    return this.variantApplied.asObservable();
  }

  getActiveVariantKey(): string | null {
    return this.activeVariantKey;
  }

  getVariants(reportKey: string): any[] {
    const r = this.reportsByKey[reportKey];
    return Array.isArray(r?.variants) ? r.variants : [];
  }

  getDefaultVariantKey(reportKey: string): string | null {
    return this.reportsByKey[reportKey]?.defaultVariantKey || null;
  }

  findVariant(reportKey: string, variantKey: string | null): any | null {
    if (!variantKey) return null;
    const variants = this.getVariants(reportKey);
    return variants.find(v => v.key === variantKey) || null;
  }

  // Snapshot of the current view state suitable for storing in a variant.
  // Search and date range are intentionally excluded.
  getCurrentVariantState() {
    return {
      activeFields: Array.isArray(this.activeFields)
        ? this.activeFields.map(f => f.path) : [],
      sorts: Array.isArray(this.sorts) ? this.sorts.map(s => ({ path: s.path, direction: s.direction })) : [],
      valueFilters: Array.isArray(this.valueFilters)
        ? this.valueFilters.map(f => ({ ...f })) : [],
    };
  }

  // Resolves the variant for the active report and applies its state, then reloads.
  // Pass null/'system' to reset to the report template's defaults.
  applyVariantByKey(variantKey: string | null) {
    if (!this.activeReportConfig) return;
    const reportKey = this.activeReportConfig.key;
    const variant = (variantKey && variantKey !== 'system')
      ? this.findVariant(reportKey, variantKey)
      : null;
    this.applyVariant(variant);
  }

  applyVariant(variant: any | null) {
    if (!this.activeReportConfig) return;
    const reportKey = this.activeReportConfig.key;
    this.activeVariantKey = variant?.key || null;

    if (variant) {
      this.sorts = Array.isArray(variant.sorts) && variant.sorts.length > 0
        ? variant.sorts.map(s => ({ path: s.path, direction: s.direction === -1 ? -1 : 1 }))
        : null;
      this.valueFilters = Array.isArray(variant.valueFilters)
        ? variant.valueFilters.map(f => ({ ...f }))
        : [];
      this.activeReportConfig.clientConfig = this.activeReportConfig.clientConfig || {};
      this.activeReportConfig.clientConfig.activeFields = Array.isArray(variant.activeFields)
        ? [...variant.activeFields] : null;
    } else {
      // System default: clear any saved overrides
      this.sorts = null;
      this.valueFilters = [];
      if (this.activeReportConfig.clientConfig) {
        delete this.activeReportConfig.clientConfig.activeFields;
      }
    }

    // Force loadReport's initial-load branch to rebuild activeFields from server response
    this.activeFields = null;
    this.records = null;
    this.totals = null;
    this.state.skip = null;

    this.valueFiltersUpdated.next(this.valueFilters);
    this.sortSet.next(this.sorts);
    this.variantApplied.next({ reportKey, variantKey: this.activeVariantKey });

    this.loadReport(null);
  }

  async saveCurrentAsVariant(name: string): Promise<any> {
    if (!this.activeReportConfig) throw new Error('No active report');
    const reportKey = this.activeReportConfig.key;
    const state = this.getCurrentVariantState();
    const result: any = await this.ioService.post('/report/saveReportVariant', {
      reportKey,
      variant: { name, ...state },
    });
    this.applyServerVariantResponse(reportKey, result);
    if (result.variant?.key) {
      this.activeVariantKey = result.variant.key;
      this.variantApplied.next({ reportKey, variantKey: this.activeVariantKey });
    }
    return result.variant;
  }

  async updateVariantWithCurrentState(variantKey: string): Promise<any> {
    if (!this.activeReportConfig) throw new Error('No active report');
    const reportKey = this.activeReportConfig.key;
    const existing = this.findVariant(reportKey, variantKey);
    if (!existing) throw new Error('Variant not found');
    const state = this.getCurrentVariantState();
    const result: any = await this.ioService.post('/report/saveReportVariant', {
      reportKey,
      variant: { key: variantKey, name: existing.name, ...state },
    });
    this.applyServerVariantResponse(reportKey, result);
    return result.variant;
  }

  async renameVariant(variantKey: string, name: string): Promise<any> {
    if (!this.activeReportConfig) throw new Error('No active report');
    const reportKey = this.activeReportConfig.key;
    const existing = this.findVariant(reportKey, variantKey);
    if (!existing) throw new Error('Variant not found');
    // Preserve existing state; only the name changes
    const result: any = await this.ioService.post('/report/saveReportVariant', {
      reportKey,
      variant: {
        key: variantKey,
        name,
        activeFields: existing.activeFields || [],
        sorts: existing.sorts || [],
        valueFilters: existing.valueFilters || [],
      },
    });
    this.applyServerVariantResponse(reportKey, result);
    return result.variant;
  }

  async deleteVariant(variantKey: string): Promise<void> {
    if (!this.activeReportConfig) throw new Error('No active report');
    const reportKey = this.activeReportConfig.key;
    const result: any = await this.ioService.post('/report/deleteReportVariant', {
      reportKey,
      variantKey,
    });
    this.applyServerVariantResponse(reportKey, result);
    if (this.activeVariantKey === variantKey) {
      this.activeVariantKey = null;
      this.variantApplied.next({ reportKey, variantKey: null });
    }
  }

  async setDefaultVariant(variantKey: string | null): Promise<void> {
    if (!this.activeReportConfig) throw new Error('No active report');
    const reportKey = this.activeReportConfig.key;
    const result: any = await this.ioService.post('/report/setDefaultReportVariant', {
      reportKey,
      variantKey,
    });
    if (this.reportsByKey[reportKey]) {
      this.reportsByKey[reportKey].defaultVariantKey = result.defaultVariantKey || null;
    }
    this.variantsChanged.next({ reportKey });
    // Re-emit categories so menu subscribers see the updated default
    this.reportsLoaded.next({ reportCategories: this.reportCategories });
  }

  private applyServerVariantResponse(reportKey: string, result: any) {
    const target = this.reportsByKey[reportKey];
    if (!target) return;
    if (Array.isArray(result.variants)) target.variants = result.variants;
    if (Object.prototype.hasOwnProperty.call(result, 'defaultVariantKey')) {
      target.defaultVariantKey = result.defaultVariantKey || null;
    }
    this.variantsChanged.next({ reportKey });
    // Re-emit categories so the side nav updates
    this.reportsLoaded.next({ reportCategories: this.reportCategories });
  }

  getLoadRecordsInput() {
    let loadRecordsInput: any = {
      utcOffset: new Date().getTimezoneOffset(),
      // IANA zone, so cached report chunks line up with local days across DST
      // (the offset alone is today's, not the span's).
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    let clientFilters = [];

    // Add Location Filter
    const activeLocationId = this.locationService.getActiveLocation()?._id;
    if (activeLocationId) {
      clientFilters.push({
        path: 'locationId',
        type: 'id',
        value: [
          activeLocationId
        ]
      });
    }

    // Add Date Range - use the template's datePath if available,
    // otherwise fall back to the cached report's datePath from getLocationReports.
    const datePath = this.state?.template?.datePath
      || this.reportsByKey[this.activeReportConfig?.key]?.datePath;

    if (datePath && this.state.dateRange?.startDate) {
      clientFilters.push({
        path: datePath,
        type: 'dateTime',
        value: {
          start: this.state.dateRange.startDate,
          end: this.state.dateRange.endDate
        }
      });
    }

    // Add Value Filters
    if (this.valueFilters) {
      clientFilters = clientFilters.concat(this.valueFilters);
    }

    const clientConfig: any = {
      clientFilters,
      sort: this.sorts,
      search: this.search,
      activeFields: null,
      download: false,
    };



    // Apply skip if relevant
    if (this.state.skip != null) {
      clientConfig.skip = this.state.skip;
    }

    // Optionaly define a custom set of optional fields to be returned from the server
    if (this.activeFields) {
      clientConfig.activeFields = [];
      for (const activeField of this.activeFields) {
        clientConfig.activeFields.push(activeField.path);
      }
    } else if (this.activeReportConfig?.clientConfig?.activeFields) {
      clientConfig.activeFields = this.activeReportConfig.clientConfig.activeFields;
    }

    loadRecordsInput.clientConfig = clientConfig;
    
    if (this.activeReportConfig._id) {
      loadRecordsInput.reportId = this.activeReportConfig._id;
    } else if (this.activeReportConfig.key) {
      loadRecordsInput.key = this.activeReportConfig.key;
    }

    console.log('getLoadRecordsInput: ', loadRecordsInput);

    return loadRecordsInput;
  }


  /**
   * Downloads the active report. Without a templateKey this is the standard CSV
   * export. With one, the server renders the matching export template (PDF,
   * pipe-delimited, etc.) and the extension names the saved file accordingly.
   */
  download(templateKey: string = null, extension: string = 'csv', fileName: string = null) {
    if (this.activeReportConfig) {

      let loadRecordsInput: any = this.getLoadRecordsInput();
      loadRecordsInput.clientConfig.download = true;
      if (templateKey) {
        loadRecordsInput.templateKey = templateKey;
      }

      // Retrieve data from the server. A template may dictate the exact file name
      // (partner feeds such as IRI's allstoresiri.txt); otherwise report name + extension.
      return this.ioService.download('/report/loadRecords', loadRecordsInput, fileName || (this.state.template.name + '.' + extension));
    } else {
      console.log('No active report to download');
    }
  }

  printReport(templateKey) {
    if (this.activeReportConfig) {

      let loadRecordsInput:any = this.getLoadRecordsInput();
      loadRecordsInput.clientConfig.download = true;
      loadRecordsInput.templateKey = templateKey;

      // Tags/labels go silently to the store's tag printer when one is online
      // (TagPrintService), else the popup + print dialog.
      this.tagPrintService.print('report/loadRecords', loadRecordsInput, {
        description: this.activeReportConfig.name,
      }).catch((err) => {
        AppComponent.showSnack(err?.error?.msg || 'Unable to generate print output.', 'Dismiss', 5000);
      });
    } else {
      console.log('No active report to print');
    }
  }


  refresh() {
    // Location/auth services can emit "active changed" during boot, which
    // triggers refresh() from the report component's subscription before
    // setActiveReport has run. Bail rather than load with no active report.
    if (!this.activeReportConfig) return;
    this.state.skip = null;
    this.totals = null;
    this.records = null;
    this.recordsUpdated.next(this.records);
    this.loadReport(null);
    // this.sortSet.next(this.sorts);
  }


  loadReportWithContext = async (context) => {
    console.log('loadReportWithContext - context: ', context);

    this.stagedConfig = {
      valueFilters: context.filters || null,
      activeFields: context.activeFields || null,
      name: context.name || null,
    };

    this.activeReportConfig = {
      _id: context.reportId
    };

    await this.navigateToReport(context.key);
  }


}
